import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { HttpResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { UserGroupService } from '@pulpo/pulpo-api';
import { UserGroup, UserGroupTypeEnum, hasUsers } from '@pulpo/pulpo-models';
import {
  clearPossibleSubscriptions,
  compareNames,
  removeAccentsAndTurnToLowerCase,
} from '@pulpo/pulpo-commons';

/**
 * Sections of the option list, in display order. Also drives the sort: a group
 * is ranked by the first section it belongs to.
 */
const GROUP_SECTIONS: { type: UserGroupTypeEnum; label: string }[] = [
  { type: UserGroupTypeEnum.AGENCY, label: 'Agences' },
  { type: UserGroupTypeEnum.SUPPLIER, label: 'Distributeurs' },
  { type: UserGroupTypeEnum.PRODUCER, label: 'Producteurs' },
];

/** Groups carrying no known type still have to be reachable. */
const OTHER_SECTION_LABEL = 'Autres';

interface GroupSection {
  label: string;
  groups: UserGroup[];
}

@Component({
  standalone: false,
  selector: 'app-user-filter',
  templateUrl: './user-filter.component.html',
  styleUrls: ['./user-filter.component.scss'],
})
export class UserFilterComponent implements OnInit, OnDestroy {
  @Output() filterEvent: EventEmitter<any> = new EventEmitter<any>();
  filterForm: FormGroup;
  /** Every group holding users, agencies included, sorted by name. */
  groups: UserGroup[] = [];
  /** Groups matching what is currently typed, split into labelled sections. */
  filteredSections: GroupSection[] = [];

  private groupsSubscription: Subscription;
  private formSubscription: Subscription;
  private groupSearchSubscription: Subscription;

  constructor(
    private fb: FormBuilder,
    private userGroupService: UserGroupService
  ) {}

  ngOnInit(): void {
    this.initFilterForm();
    this.loadGroups();

    // One request per keystroke otherwise: the text fields feed the server-side
    // query directly.
    this.formSubscription = this.filterForm.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(
          (previous, current) =>
            JSON.stringify(previous) === JSON.stringify(current)
        )
      )
      .subscribe(() => this.emitFilter());

    // Not debounced: the option list must follow the typing, only the server
    // query waits.
    this.groupSearchSubscription = this.filterForm.controls[
      'group'
    ].valueChanges.subscribe((group) => this.filterGroups(group));
  }

  ngOnDestroy(): void {
    clearPossibleSubscriptions(
      this.groupsSubscription,
      this.formSubscription,
      this.groupSearchSubscription
    );
  }

  private loadGroups(): void {
    this.groupsSubscription = this.userGroupService
      .getAvailableGroups()
      .subscribe({
        next: (res: HttpResponse<UserGroup[]>) => {
          // Every group type is offered, not only agencies: users are attached
          // to producer and supplier groups too. Groups holding no user are
          // dropped, they can never yield a result (PUL-3022).
          this.groups = (res.body || [])
            .filter((g: UserGroup) => hasUsers(g))
            .sort(
              (a: UserGroup, b: UserGroup) =>
                this.typeRank(a) - this.typeRank(b) ||
                compareNames(a.name, b.name)
            );
          this.filterGroups(this.filterForm.value.group);
        },
        error: (err: any) => {
          console.error('Error loading user groups:', err);
        },
      });
  }

  private initFilterForm(): void {
    this.filterForm = this.fb.group({
      email: [],
      firstName: [],
      lastName: [],
      // Holds the typed text while searching, then the selected UserGroup.
      group: [],
      activated: [''],
    });
  }

  /**
   * Emits the server-side query params. Built explicitly rather than from
   * `filterForm.value`: the group control carries a UserGroup, the query needs
   * its id, and a half-typed group name must not filter anything.
   *
   * `agencyGroupId` keeps its name because that is the field the user service
   * exposes; it matches any group id, agency or not.
   */
  private emitFilter(): void {
    const { email, firstName, lastName, group, activated } =
      this.filterForm.value;
    this.filterEvent.emit({
      email,
      firstName,
      lastName,
      agencyGroupId: this.selectedGroupId(group),
      activated,
    });
  }

  private selectedGroupId(group: UserGroup | string | null): string | null {
    return group && typeof group !== 'string' ? group.id : null;
  }

  /**
   * Once a group is picked, `group` holds the full UserGroup object rather
   * than typed text: narrowing the list to its name would hide every other
   * group behind the just-selected one on the next open. Only free-typed text
   * narrows the list; a selection (or no selection) shows everything.
   */
  private filterGroups(group: UserGroup | string | null): void {
    const term =
      typeof group === 'string'
        ? removeAccentsAndTurnToLowerCase(group).trim()
        : '';
    const matching = !term
      ? this.groups
      : this.groups.filter((g) =>
          removeAccentsAndTurnToLowerCase(g.name ?? '').includes(term)
        );

    // Empty sections are dropped so a search never leaves a dangling header.
    const labels = [
      ...GROUP_SECTIONS.map((section) => section.label),
      OTHER_SECTION_LABEL,
    ];
    this.filteredSections = labels
      .map((label) => ({
        label,
        groups: matching.filter((g) => this.sectionLabel(g) === label),
      }))
      .filter((section) => section.groups.length > 0);
  }

  private sectionLabel(group: UserGroup): string {
    return GROUP_SECTIONS[this.typeRank(group)]?.label ?? OTHER_SECTION_LABEL;
  }

  /** Whether this option is the group currently held by the form control. */
  isSelectedGroup(group: UserGroup): boolean {
    const current = this.filterForm.value.group;
    return !!current && typeof current !== 'string' && current.id === group.id;
  }

  /** Keeps the group name in the input while the control holds the group. */
  displayGroup(group?: UserGroup | string): string {
    if (!group) {
      return '';
    }
    return typeof group === 'string' ? group : group.name;
  }

  /**
   * Section index of a group. A group may hold several types, the first one
   * listed in GROUP_SECTIONS wins; untyped groups land in 'Autres'.
   */
  private typeRank(group: UserGroup): number {
    const ranks = (group.types ?? [])
      .map((type) => GROUP_SECTIONS.findIndex((s) => s.type === type))
      .filter((rank) => rank >= 0);
    return ranks.length ? Math.min(...ranks) : GROUP_SECTIONS.length;
  }

  clearGroup(): void {
    this.filterForm.get('group')?.setValue(null);
  }

  onResetFilters(): void {
    // `reset()` rather than rebuilding the group: `valueChanges` is subscribed
    // to this very instance.
    this.filterForm.reset({ activated: '' });
  }
}
